import { getOneButtonAppId, type StripeContractAmount } from '../services/oneButtonServices/stripeServices';

export type StripePageInput = {
    application_Id: number;
    contractId: number;
    opportunityId: number;
    salesForceId: string;
    productType: string;
    customerAddress: string;
    customerPhone: string;
    customerEmail: string;
    totalEpc: number;
    totalAmount: number;
    customer_Name: string;
    salesperson_Name: string;
    salesperson_Phone: string;
    salesperson_Email: string;
    productTypeList: string[];
    salesforce_Opportunity_Id: string;
    StripeContractAmount: StripeContractAmount[];
    appName: 'oneBUTTON';
    Contact_Last_Name: string;
    Contact_First_Name: string;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const pickStr = (row: Record<string, unknown> | null | undefined, keys: string[]): string => {
    if (!row) return '';
    for (const key of keys) {
        const value = row[key];
        if (value != null && String(value).trim() !== '') return String(value);
    }
    return '';
};

const isTruthyFlag = (value: unknown): boolean => {
    if (value === true || value === 1) return true;
    const s = String(value ?? '').trim().toLowerCase();
    return s === 'true' || s === 'yes' || s === '1';
};

/** V1 `setStripeInput`: `contractId`/`opportunityId` are 0; list query uses Salesforce Id. */
export function buildStripePageInput(
    opportunityDetails: unknown,
    salesForceId: string | null | undefined,
    stripeContractAmount: StripeContractAmount[] | null | undefined,
): StripePageInput {
    const root = asRecord(opportunityDetails) ?? {};
    const nested = asRecord(root.OpportunityDetails) ?? asRecord(root.opportunityDetails) ?? root;
    const interest = asRecord(root.OpportunityInterest) ?? asRecord(nested.OpportunityInterest) ?? {};
    const salesPerson =
        asRecord(root.SalesPerson) ??
        asRecord(root.salesPerson) ??
        asRecord(nested.SalesPerson) ??
        {};
    const addr =
        asRecord(root.InstallationAddress) ??
        asRecord(root.installationAddress) ??
        asRecord(nested.InstallationAddress) ??
        {};

    const customers = Array.isArray(root.Customers)
        ? root.Customers
        : Array.isArray(root.customers)
          ? root.customers
          : [];
    const primary =
        asRecord(
            customers.find((item) => {
                const contact = asRecord(item);
                return Boolean(contact?.IsPrimaryContact || contact?.isPrimaryContact);
            }) ?? customers[0],
        ) ?? {};

    let address = pickStr(nested, ['Address', 'address']);
    const city = pickStr(nested, ['City', 'city']) || pickStr(addr, ['City', 'city']);
    const state = pickStr(nested, ['State', 'state']) || pickStr(addr, ['stateCode', 'State', 'state']);
    if (!address) {
        address = [pickStr(addr, ['Street', 'street']), city, state].filter(Boolean).join(', ');
    } else {
        if (city) address += `, ${city}`;
        if (state) address += `, ${state}`;
    }

    let productType = '';
    if (isTruthyFlag(interest.Solar)) productType = 'Solar';
    else if (isTruthyFlag(interest.Battery) || isTruthyFlag(interest.BatteryOnly)) productType = 'Battery';
    else if (
        isTruthyFlag(interest.Roof) ||
        isTruthyFlag(interest.RoofOnly) ||
        isTruthyFlag(interest.RRR) ||
        isTruthyFlag(interest.RR)
    ) {
        productType = 'Roof';
    }

    const productsRaw = Array.isArray(root.opportunityProduct)
        ? root.opportunityProduct
        : Array.isArray(root.OpportunityProduct)
          ? root.OpportunityProduct
          : [];
    const productTypeList: string[] = [];
    productsRaw.forEach((element: unknown) => {
        const row = asRecord(element);
        if (!row) return;
        const active = row.ActiveFlag ?? row.activeFlag ?? row.Active_Ind ?? row.active_Ind;
        if (!isTruthyFlag(active)) return;
        const name = pickStr(row, ['Product_Type_Name', 'product_Type_Name']);
        if (name) productTypeList.push(name);
    });

    const firstName = pickStr(nested, ['FirstName', 'firstName']) || pickStr(primary, ['FirstName', 'firstName']);
    const lastName = pickStr(nested, ['LastName', 'lastName']) || pickStr(primary, ['LastName', 'lastName']);
    const customerName =
        pickStr(nested, ['Name', 'name']) ||
        [firstName, lastName].filter(Boolean).join(' ') ||
        pickStr(primary, ['Name', 'name']);
    const customerNo = pickStr(nested, ['Customer_No', 'customer_No', 'CustomerNo']);
    const sfId =
        pickStr(nested, ['SalesForce_Id', 'salesForce_Id', 'SalesForceId']) || String(salesForceId || '').trim();

    return {
        application_Id: getOneButtonAppId(),
        contractId: 0,
        opportunityId: 0,
        salesForceId: customerNo || sfId,
        productType,
        customerAddress: address,
        customerPhone:
            pickStr(nested, ['Phone', 'phone']) || pickStr(primary, ['Phone', 'phone']),
        customerEmail:
            pickStr(nested, ['Email', 'email']) || pickStr(primary, ['Email', 'email']),
        totalEpc: 0,
        totalAmount: 0,
        customer_Name: customerName,
        salesperson_Name: pickStr(salesPerson, ['Name', 'name']),
        salesperson_Phone: pickStr(salesPerson, ['Phone', 'phone']),
        salesperson_Email: pickStr(salesPerson, ['Email', 'email']),
        productTypeList,
        salesforce_Opportunity_Id: sfId,
        StripeContractAmount: Array.isArray(stripeContractAmount) ? stripeContractAmount : [],
        appName: 'oneBUTTON',
        Contact_Last_Name: lastName,
        Contact_First_Name: firstName,
    };
}
